import React from 'react';
import ListItem from '@mui/joy/ListItem';
import type { MouseEvent } from 'react';
import { Box } from '../Box';
import { Typography } from '../Typography';
import Stack from '../../_components/Stack';
import type { TooltipProps } from '../Tooltip';
import { slowCn } from '../../lib/utils';

export enum MenuItemHelpTextPosition {
  Inline = 'inline',
  InTooltip = 'tooltip',
}

export type MenuContentProps = {
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  title: string;
  titleClassName?: string;
  helpTextDetails?: {
    text: string | React.ReactNode;
    position: MenuItemHelpTextPosition;
    placement?: TooltipProps['placement'];
  };
  helpTextClassName?: string;
  caption?: string;
  size?: 'sm' | 'md';
  onClick?: (event: MouseEvent) => void;
  onMouseOver?: (event: MouseEvent) => void;
  disabled?: boolean;
  selected?: boolean;
  className?: string;
};

function MenuContent({
  startDecorator,
  endDecorator,
  title,
  titleClassName,
  helpTextDetails,
  helpTextClassName,
  caption,
  size,
  onClick,
  onMouseOver,
  disabled,
  selected,
  className,
}: MenuContentProps) {
  return (
    <ListItem
      aria-label={title}
      className={slowCn(
        'row bg-primary hover:bg-secondary_hover my-xxs mx-sm ps-md pe-lg flex items-center rounded-sm',
        { 'cursor-pointer': onClick },
        { 'pointer-events-none': disabled },
        { 'bg-quaternary': selected },
        { 'py-md !h-9': size === 'md' },
        { 'py-sm !h-7 !min-h-7': size === 'sm' },
        className,
      )}
      onClick={onClick}
      onMouseOver={onMouseOver}
    >
      <Stack className="gap-xxs w-full">
        <Stack className="w-full" direction="row">
          <Box className="row gap-x-sm me-auto flex items-center">
            {startDecorator}
            <Typography className={titleClassName} variant={size === 'sm' ? 'text-xs' : 'text-sm'}>
              {title}
            </Typography>
          </Box>
          {helpTextDetails?.position === MenuItemHelpTextPosition.Inline ? (
            <Typography className={helpTextClassName} variant="text-xs" weight="regular">
              {helpTextDetails.text}
            </Typography>
          ) : null}
          {endDecorator}
        </Stack>
        {caption ? (
          <Stack className="w-full" direction="row">
            <Typography className="text-secondary mb-xxs" variant="text-xs" weight="regular">
              {caption}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </ListItem>
  );
}

export default MenuContent;
